import { defineComponent, getCurrentInstance, h, onMounted, ref, watch } from 'vue'

/**
 * Renders CMS-authored HTML, including embed <script>s such as the Proca widget.
 *
 * Deliberately a render function rather than `v-html`: since Vue 3.5.42 a
 * compiled `v-html` binding is re-assigned during hydration, which wipes the DOM
 * an embed has already built and leaves its <script> inert. A hand-written vnode
 * carries no dynamic props, so hydration adopts the server DOM untouched.
 *
 * Scripts inserted through innerHTML never run, so on a client-side render (SPA
 * navigation, or the html changing) each one is swapped for a fresh element.
 *
 * A classic external script without `async`/`defer` is written expecting to
 * block and run in place, the same as when the HTML parser meets it — a
 * following inline script may depend on a global it sets up. But an element
 * created with `document.createElement` defaults its `async` IDL property to
 * `true` regardless of the source markup's attributes, so without correction
 * such scripts would run out of order. For that case (and only that case —
 * explicit `async` and `type="module"` scripts are left to run in parallel,
 * as authored) `async` is set to `false` and later scripts wait for it to
 * load, or fail, before running.
 */
export default defineComponent({
  name: 'RawHtml',
  props: {
    html: { type: String, default: '' },
  },
  setup(props) {
    const root = ref<HTMLElement | null>(null)
    // Vue assigns the server-rendered element before setup runs when hydrating;
    // the browser already executed those scripts while parsing the page.
    const hydrating = !!getCurrentInstance()?.vnode.el

    const runScripts = async () => {
      for (const inert of root.value?.querySelectorAll('script') ?? []) {
        const script = document.createElement('script')
        for (const { name, value } of inert.attributes) script.setAttribute(name, value)
        script.textContent = inert.textContent

        const blocking = !!script.src && script.type !== 'module' && !inert.hasAttribute('async')
        if (!blocking) {
          inert.replaceWith(script)
          continue
        }
        script.async = false
        await new Promise<void>((resolve) => {
          script.addEventListener('load', () => resolve(), { once: true })
          script.addEventListener('error', () => resolve(), { once: true })
          inert.replaceWith(script)
        })
      }
    }

    onMounted(() => {
      if (!hydrating) void runScripts()
    })
    watch(() => props.html, () => void runScripts(), { flush: 'post' })

    return () => h('div', { ref: root, innerHTML: props.html })
  },
})
